import React from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { RegistrationInputButton } from "@/components/valuation/RegistrationInputButton";
import { useLanguage } from "@/context/LanguageContext";
import { commonLabels } from "@/i18n";

interface FinalCTAProps {
  darkOverlayOpacity: number;
  onScrollToValuation: () => void;
}

const FinalCTA = ({
  darkOverlayOpacity,
  onScrollToValuation,
}: FinalCTAProps) => {
  const { language } = useLanguage();
  const labels = commonLabels(language);
  const ctaData = labels.menuPages?.sellCar?.contactSection?.finalCTA || {};
  const statsData = ctaData.stats || [];
  const statColors = ["text-blue-600", "text-green-600", "text-purple-600"];

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.4 }}
      viewport={{ once: true }}
      className="text-center mt-16 relative z-20"
    >
      <motion.div
        className={`rounded-3xl p-12 shadow-lg max-w-4xl mx-auto transition-all duration-500 ${
          darkOverlayOpacity > 0.3
            ? "bg-white/10 backdrop-blur-sm border border-white/20"
            : "bg-white"
        }`}
        animate={{
          backgroundColor:
            darkOverlayOpacity > 0.3
              ? "rgba(255, 255, 255, 0.1)"
              : "rgba(255, 255, 255, 1)",
        }}
      >
        <h3
          className={`text-3xl font-bold mb-6 transition-colors duration-500 ${
            darkOverlayOpacity > 0.3 ? "text-white" : "text-gray-900"
          }`}
        >
          {ctaData.title || "Varför Vänta? Sälj Din Bil Idag!"}
        </h3>
        <p
          className={`text-lg mb-8 leading-relaxed transition-colors duration-500 ${
            darkOverlayOpacity > 0.3 ? "text-gray-300" : "text-gray-600"
          }`}
        >
          {ctaData.description ||
            "Med BILENIA och BEA Auktioner får du tillgång till Sveriges mest effektiva bilförsäljningsplattform."}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {statsData.map((stat: any, index: number) => (
            <div key={index} className="text-center">
              <div
                className={`text-3xl font-bold ${statColors[index] || statColors[0]} mb-2`}
              >
                {stat.value || ""}
              </div>
              <div
                className={`transition-colors duration-500 ${
                  darkOverlayOpacity > 0.3 ? "text-gray-300" : "text-gray-600"
                }`}
              >
                {stat.label || ""}
              </div>
            </div>
          ))}
        </div>

        <Button
          onClick={onScrollToValuation}
          size="lg"
          className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-12 py-4 text-xl font-semibold rounded-xl"
        >
          {ctaData.cta || "Sälja Min Bil Nu"}
        </Button>
      </motion.div>
    </motion.div>
  );
};

export default FinalCTA;
